import {
  ROAD_MAINTENANCE_INDEX,
  ROAD_MAINTENANCE_INDEX_ASSET_PATH,
  ROAD_MAINTENANCE_INDEX_PATH,
  type RoadMaintenanceIndexRegion,
} from '@fixtyer/content';
import { Link } from 'react-router';
import {
  ArticleSchema,
  BreadcrumbSchema,
  DatasetSchema,
  SEO,
} from '@/components/seo/seo';

const index = ROAD_MAINTENANCE_INDEX;

function formatIndexDate(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
}

function Metadata() {
  return (
    <div className="rounded-xl border border-surface-200 bg-white p-5 text-sm text-surface-700">
      <p>Published: {formatIndexDate(index.metadata.publishedDate)}</p>
      <p>Last reviewed: {formatIndexDate(index.metadata.reviewedDate)}</p>
      <p>Content owner: {index.metadata.editorialOwner}</p>
      <p>Dataset version: {index.metadata.datasetVersion}</p>
    </div>
  );
}

function SourceAndLimits() {
  return (
    <section className="space-y-4">
      <h2 className="font-display text-3xl font-bold text-brand-950">
        Methodology, source and limitations
      </h2>
      <p>{index.metadata.methodology}</p>
      <p>{index.metadata.scope}</p>
      <p>{index.metadata.limitations}</p>
      <p>{index.metadata.editionNote}</p>
      <p>
        Read the{' '}
        <Link className="text-brand-700 underline" to={index.metadata.datasetPath}>
          canonical dataset methodology, downloads and changelog
        </Link>
        , or check the{' '}
        <a className="text-brand-700 underline" href={index.metadata.dftSourceUrl}>
          Department for Transport source
        </a>
        .
      </p>
      <p>
        {index.metadata.attribution}{' '}
        <a className="text-brand-700 underline" href={index.metadata.licenseUrl}>
          {index.metadata.licenseName}
        </a>
        .
      </p>
      <p>
        Journalists can request the methodology note or a rapid factual check through{' '}
        <Link className="text-brand-700 underline" to="/media-enquiries">
          media enquiries
        </Link>
        .
      </p>
      <p>
        <a className="text-brand-700 underline" href={index.metadata.correctionUrl}>
          Report a factual correction
        </a>
        .
      </p>
    </section>
  );
}

function Citation({ value }: { value: string }) {
  return (
    <section>
      <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
        Copyable citation
      </h2>
      <pre className="whitespace-pre-wrap rounded-xl border border-surface-200 bg-surface-50 p-5 text-sm">
        {value}
      </pre>
    </section>
  );
}

function RegionTable({ region }: { region: RoadMaintenanceIndexRegion }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-surface-200 bg-white">
      <table className="w-full border-collapse">
        <caption className="p-4 text-left font-semibold text-brand-950">
          {region.name} DfT overall bands, 2025-2026
        </caption>
        <thead className="bg-surface-100">
          <tr>
            <th className="p-3 text-left" scope="col">Authority</th>
            <th className="p-3 text-left" scope="col">Overall band</th>
            <th className="p-3 text-left" scope="col">Council report</th>
            <th className="p-3 text-left" scope="col">Journalist brief</th>
          </tr>
        </thead>
        <tbody>
          {region.authorities.map((authority) => (
            <tr key={authority.authorityId} className="border-t border-surface-200">
              <th className="p-3 text-left font-medium" scope="row">
                {authority.displayName}
              </th>
              <td className="p-3">{authority.ratings.overall.toUpperCase()}</td>
              <td className="p-3">
                {authority.reportUrl ? (
                  <Link className="text-brand-700 underline" to={authority.reportUrl}>
                    View report
                  </Link>
                ) : (
                  'No Fixtyer report published'
                )}
              </td>
              <td className="p-3">
                <a
                  className="text-brand-700 underline"
                  href={`${ROAD_MAINTENANCE_INDEX_ASSET_PATH}/authority-briefs/${authority.slug}.html`}
                >
                  Data brief
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RoadMaintenanceIndexPage() {
  const { counts } = index.national;
  return (
    <div className="min-h-screen bg-surface-50">
      <SEO
        title="Fixtyer Road Maintenance Index Q3 2026 | Fixtyer"
        description="Deterministic DfT road maintenance band counts for all 154 rated English highway authorities, with regional tables, methodology and downloads."
        canonical={ROAD_MAINTENANCE_INDEX_PATH}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Road Maintenance Index', url: ROAD_MAINTENANCE_INDEX_PATH },
        ]}
      />
      <DatasetSchema
        name={`${index.metadata.title} ${index.metadata.edition}`}
        description="A deterministic national and regional presentation of the DfT 2025-2026 road maintenance rating bands for England."
        url={ROAD_MAINTENANCE_INDEX_PATH}
        datePublished={index.metadata.publishedDate}
        dateModified={index.metadata.reviewedDate}
        license={index.metadata.licenseUrl}
        temporalCoverage="2025-2026"
        spatialCoverage="England"
        keywords={['DfT road maintenance ratings', 'England', 'Q3 2026']}
        variableMeasured={['Overall GREEN band count', 'Overall AMBER band count', 'Overall RED band count']}
        distribution={[
          {
            name: 'Canonical ratings CSV',
            contentUrl: index.metadata.datasetCsvPath,
            encodingFormat: 'text/csv',
          },
          {
            name: 'Canonical ratings JSON',
            contentUrl: index.metadata.datasetJsonPath,
            encodingFormat: 'application/json',
          },
        ]}
      />

      <header className="bg-brand-950 text-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <p className="font-semibold text-amber-300">Q3 2026 edition · England only</p>
          <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">
            Fixtyer Road Maintenance Index
          </h1>
          <p className="mt-5 max-w-3xl text-lg text-brand-100">
            The DfT rated 154 English highway authorities: {counts.green} GREEN,{' '}
            {counts.amber} AMBER and {counts.red} RED overall. These are transparent
            counts of official bands, not a composite score or ranking.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-12 px-4 py-12 sm:px-6">
        <Metadata />
        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            National findings
          </h2>
          <p className="mb-5">154 rated English highway authorities in dataset 2025-2026-v1.</p>
          <div className="overflow-x-auto rounded-xl border border-surface-200 bg-white">
            <table className="w-full">
              <caption className="p-4 text-left font-semibold">
                Overall DfT band counts for England
              </caption>
              <thead className="bg-surface-100">
                <tr>
                  <th className="p-3 text-left" scope="col">Band</th>
                  <th className="p-3 text-left" scope="col">Authorities</th>
                </tr>
              </thead>
              <tbody>
                {(['green', 'amber', 'red'] as const).map((band) => (
                  <tr key={band} className="border-t border-surface-200">
                    <th className="p-3 text-left" scope="row">{band.toUpperCase()}</th>
                    <td className="p-3">{counts[band]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Regional editions
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {index.regions.map((region) => (
              <li key={region.slug} className="rounded-xl border border-surface-200 bg-white p-4">
                <Link
                  className="font-semibold text-brand-700 underline"
                  to={`${ROAD_MAINTENANCE_INDEX_PATH}/regions/${region.slug}`}
                >
                  {region.name}
                </Link>
                <p className="mt-1 text-sm text-surface-600">
                  {region.authorityCount} authorities: {region.counts.green} GREEN,{' '}
                  {region.counts.amber} AMBER, {region.counts.red} RED
                </p>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Journalist downloads
          </h2>
          <div className="flex flex-wrap gap-4">
            <a className="text-brand-700 underline" href={`${ROAD_MAINTENANCE_INDEX_ASSET_PATH}/national-findings.json`}>
              National findings JSON
            </a>
            <a className="text-brand-700 underline" href={`${ROAD_MAINTENANCE_INDEX_ASSET_PATH}/embeds/national.html`}>
              Accessible national table
            </a>
            <a className="text-brand-700 underline" href={`${ROAD_MAINTENANCE_INDEX_ASSET_PATH}/manifest.json`}>
              Asset manifest
            </a>
          </div>
        </section>

        <SourceAndLimits />
        <Citation value={index.citation} />
        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Edition changelog
          </h2>
          <p>
            {index.metadata.edition}, published{' '}
            {formatIndexDate(index.metadata.publishedDate)}: first edition tied to
            dataset {index.metadata.datasetVersion}, with national and all manifest-derived regional counts,
            authority briefs and accessible embeds.
          </p>
        </section>
      </main>
    </div>
  );
}

export function RoadMaintenanceIndexRegionPage({
  region,
}: {
  region: RoadMaintenanceIndexRegion;
}) {
  const route = `${ROAD_MAINTENANCE_INDEX_PATH}/regions/${region.slug}`;
  return (
    <div className="min-h-screen bg-surface-50">
      <SEO
        title={`${region.name} Road Maintenance Index Q3 2026 | Fixtyer`}
        description={`${region.name} DfT road maintenance band counts and authority table for the Fixtyer Q3 2026 edition.`}
        canonical={route}
        type="article"
        article={{
          publishedTime: index.metadata.publishedDate,
          modifiedTime: index.metadata.reviewedDate,
          author: 'Fixtyer',
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Road Maintenance Index', url: ROAD_MAINTENANCE_INDEX_PATH },
          { name: region.name, url: route },
        ]}
      />
      <ArticleSchema
        title={`${region.name} Road Maintenance Index Q3 2026`}
        description={`DfT overall road maintenance band counts for ${region.authorityCount} rated authorities in ${region.name}.`}
        url={route}
        datePublished={index.metadata.publishedDate}
        dateModified={index.metadata.reviewedDate}
        author="Fixtyer"
      />
      <header className="bg-brand-950 text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <Link className="text-amber-300 underline" to={ROAD_MAINTENANCE_INDEX_PATH}>
            Fixtyer Road Maintenance Index
          </Link>
          <h1 className="mt-3 font-display text-4xl font-bold">
            {region.name}: Q3 2026 edition
          </h1>
          <p className="mt-4 text-lg text-brand-100">
            {region.authorityCount} rated authorities: {region.counts.green} GREEN,{' '}
            {region.counts.amber} AMBER and {region.counts.red} RED overall.
          </p>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-12 px-4 py-12 sm:px-6">
        <Metadata />
        <RegionTable region={region} />
        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Regional journalist assets
          </h2>
          <div className="flex flex-wrap gap-4">
            <a className="text-brand-700 underline" href={`${ROAD_MAINTENANCE_INDEX_ASSET_PATH}/regional-releases/${region.slug}.html`}>
              Regional release
            </a>
            <a className="text-brand-700 underline" href={`${ROAD_MAINTENANCE_INDEX_ASSET_PATH}/embeds/${region.slug}.html`}>
              Accessible regional table
            </a>
          </div>
        </section>
        <SourceAndLimits />
        <Citation value={region.citation} />
      </main>
    </div>
  );
}
